import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { getApplication, me } from '../api/applicant'
import type { Account, Application, DocumentSummary, SessionResponse } from '../api/types'
import { useFormStore } from '../store/formStore'
import { resumeRoute } from './sections'

type SessionStatus = 'loading' | 'anon' | 'authed'

interface SessionContextValue {
  status: SessionStatus
  account: Account | null
  application: Application | null
  /** Server-persisted document summaries; screens seed their upload-slot
   *  done-states from these on reload. */
  documents: DocumentSummary[]
  /** Re-fetch document summaries — call after a successful upload so a later
   *  same-session remount of the step (e.g. Review's "Edit") sees the file
   *  already there instead of showing an empty upload slot. */
  refreshDocuments: () => Promise<void>
  /** Install a session client-side after a successful /auth/verify. */
  applySession: (session: SessionResponse) => void
  /** Update the cached application (after PATCH/submit responses). */
  applyApplication: (application: Application | null) => void
  /** Drop the client-side session after logout or a 401. */
  clearSession: () => void
  magicLinkFailed: boolean
}

const SessionContext = createContext<SessionContextValue | null>(null)

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside SessionProvider')
  return ctx
}

export function SessionProvider({ children, magicSession }: { children: ReactNode; magicSession: Promise<SessionResponse> | null }) {
  const [status, setStatus] = useState<SessionStatus>('loading')
  const [account, setAccount] = useState<Account | null>(null)
  const [application, setApplication] = useState<Application | null>(null)
  const [documents, setDocuments] = useState<DocumentSummary[]>([])
  const [magicLinkFailed, setMagicLinkFailed] = useState(false)
  const hydrateFromServer = useFormStore((s) => s.hydrateFromServer)
  const seedAuthEmail = useFormStore((s) => s.seedAuthEmail)

  useEffect(() => {
    let cancelled = false
    const sessionRequest = magicSession ?? me()
    sessionRequest
      .then(async (session) => {
        // Load document summaries before flipping to authed so the screens'
        // one-shot slot rehydration sees them on mount.
        let docs: DocumentSummary[] = []
        if (session.application) {
          try {
            docs = (await getApplication()).documents
          } catch {
            // Slot states fall back to idle; the form data itself still loads.
          }
        }
        if (cancelled) return
        hydrateFromServer(session.application)
        // After hydration (which rebuilds formData from the server), make sure
        // the session email is present — magic-link logins and fresh browsers
        // otherwise show a blank email field.
        seedAuthEmail(session.account.email)
        setAccount(session.account)
        setApplication(session.application)
        setDocuments(docs)
        setStatus('authed')
      })
      .catch(() => {
        if (!cancelled) {
          setMagicLinkFailed(magicSession !== null)
          setStatus('anon')
        }
      })
    return () => {
      cancelled = true
    }
  }, [hydrateFromServer, magicSession, seedAuthEmail])

  const applySession = useCallback(
    (session: SessionResponse) => {
      hydrateFromServer(session.application)
      seedAuthEmail(session.account.email)
      setAccount(session.account)
      setApplication(session.application)
      setStatus('authed')
      // Returning applicants who re-verify may already have documents; fetch
      // in the background (screens rehydrate when these arrive).
      setDocuments([])
      if (session.application) {
        getApplication()
          .then((res) => setDocuments(res.documents))
          .catch(() => {})
      }
    },
    [hydrateFromServer, seedAuthEmail],
  )

  const applyApplication = useCallback((app: Application | null) => {
    setApplication(app)
  }, [])

  const refreshDocuments = useCallback(async () => {
    try {
      setDocuments((await getApplication()).documents)
    } catch {
      // Best-effort: the just-uploaded slot may show as idle after an "Edit"
      // navigation until the next successful refresh (e.g. a reload).
    }
  }, [])

  const clearSession = useCallback(() => {
    setAccount(null)
    setApplication(null)
    setStatus('anon')
  }, [])

  const value = useMemo(
    () => ({ status, account, application, documents, applySession, applyApplication, clearSession, refreshDocuments, magicLinkFailed }),
    [status, account, application, documents, applySession, applyApplication, clearSession, refreshDocuments, magicLinkFailed],
  )

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

/** Full-viewport blank while /auth/me is in flight; avoids flashing screens. */
function SessionPending() {
  return <div style={{ minHeight: '100vh', background: 'var(--color-surface-base)' }} />
}

/** Flow screens: session required. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useSession()
  if (status === 'loading') return <SessionPending />
  if (status === 'anon') return <Navigate to="/login" replace />
  return children
}

/** Form-step screens: session plus an editable (non-submitted) application.
 *  Once the current application is submitted the form is read-only, so these
 *  routes bounce straight to My Applications — where a new application can be
 *  started deliberately — instead of waiting for an auto-save to hit 409. */
export function RequireDraft({ children }: { children: ReactNode }) {
  const { status, application } = useSession()
  if (status === 'loading') return <SessionPending />
  if (status === 'anon') return <Navigate to="/login" replace />
  if (application && (application.status === 'submitted' || application.submitted_at)) {
    return <Navigate to="/applications" replace />
  }
  return children
}

/** Auth screens: bounce signed-in users to wherever they left off. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { status, application } = useSession()
  if (status === 'loading') return <SessionPending />
  if (status === 'authed') return <Navigate to={resumeRoute(application)} replace />
  return children
}

/** Index route: send everyone to the right place. */
export function BootGate() {
  const { status, application, magicLinkFailed } = useSession()
  if (status === 'loading') return <SessionPending />
  if (magicLinkFailed) return <Navigate to="/auth/error?reason=invalid" replace />
  if (status === 'anon') return <Navigate to="/login" replace />
  return <Navigate to={resumeRoute(application)} replace />
}
